import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useToast,
  Checkbox,
  Flex,
  Text,
  Input,
  Select,
  HStack,
  VStack,
  useColorModeValue,
  Spinner,
  Badge,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  SimpleGrid,
  InputGroup,
  InputLeftElement,
  Icon,
  Tooltip,
} from '@chakra-ui/react';
import { 
  ChevronDownIcon, 
  ChevronUpIcon, 
  SearchIcon, 
  ArrowBackIcon,
  DownloadIcon,
  RepeatIcon,
  ViewIcon,
  ExternalLinkIcon,
  CheckIcon
} from '@chakra-ui/icons';
import { getToken } from '../../../../../../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../../../context/AuthContext';
import PrecatoriosSelecionadasModal from './PrecatoriosSelecionadasModal';
import useHandleOpenDetails from './useHandleOpenDetails';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? process.env.REACT_APP_VERCEL_API_URL 
  : process.env.REACT_APP_LOCAL_API_URL;

const PrecatoriosSelecionadasSP = () => {
  const [precatorios, setPrecatorios] = useState([]);
  const [filteredPrecatorios, setFilteredPrecatorios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterField, setFilterField] = useState('numero_precatorio');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('ordem');
  const [sortOrder, setSortOrder] = useState('asc');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { user } = useAuth();
  
  const toast = useToast();
  const navigate = useNavigate();
  
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const headingColor = useColorModeValue('gray.700', 'white');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const sectionBg = useColorModeValue('gray.50', 'gray.900');
  const selectedBg = useColorModeValue('blue.50', 'blue.900');
  const theadBg = useColorModeValue('gray.50', 'gray.900');

  const { selectedPrecatorio, detailsLoading, handleOpenDetails } = useHandleOpenDetails();

  useEffect(() => {
    fetchPrecatorios();
  }, []);

  useEffect(() => {
    filterAndSortPrecatorios();
  }, [precatorios, searchTerm, filterField, statusFilter, sortField, sortOrder]);

  const fetchPrecatorios = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/precatorioSP/selecionados`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao buscar precatórios');
      }

      const data = await response.json();
      setPrecatorios(data.data || []);
    } catch (error) {
      console.error('Erro:', error);
      toast({
        title: 'Erro ao carregar precatórios',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortPrecatorios = () => {
    let filtered = [...precatorios];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(item => {
        const value = item[filterField]?.toString().toLowerCase() || '';
        return value.includes(searchTerm.toLowerCase());
      });
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => item.status === statusFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle numeric fields
      if (sortField === 'valor_atualizado' || sortField === 'ordem') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredPrecatorios(filtered);
  };

  const handleSelectItem = (id) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const newSelected = new Set();
      filteredPrecatorios.forEach(item => newSelected.add(item._id));
      setSelectedItems(newSelected);
    } else {
      setSelectedItems(new Set());
    }
  };

  const handleBulkStatusUpdate = async (newStatus) => {
    if (selectedItems.size === 0) {
      toast({
        title: 'Nenhum item selecionado',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/precatorioSP/update-status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ids: Array.from(selectedItems),
          status: newStatus
        })
      });

      if (!response.ok) {
        throw new Error('Erro ao atualizar status');
      }

      const result = await response.json();
      
      toast({
        title: 'Status atualizado',
        description: result.message,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setSelectedItems(new Set());
      fetchPrecatorios();
    } catch (error) {
      console.error('Erro:', error);
      toast({
        title: 'Erro ao atualizar status',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDesselecionar = async () => {
    if (selectedItems.size === 0) {
      toast({
        title: 'Nenhum item selecionado',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/precatorioSP/update-selection`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ids: Array.from(selectedItems),
          selected: false
        })
      });

      if (!response.ok) {
        throw new Error('Erro ao desselecionar');
      }

      const result = await response.json();
      
      toast({
        title: 'Itens desselecionados',
        description: result.message,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setSelectedItems(new Set());
      fetchPrecatorios();
    } catch (error) {
      console.error('Erro:', error);
      toast({
        title: 'Erro ao desselecionar',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDownloadSelected = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/precatorioSP/download-selected`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao baixar arquivo');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'precatorios_selecionados_sp.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Erro:', error);
      toast({
        title: 'Erro ao baixar arquivo',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Não Contatou': 'gray',
      'Pendente': 'yellow',
      'Em Análise': 'blue',
      'Proposta Enviada': 'purple',
      'Proposta Aceita': 'green',
      'Contrato Assinado': 'teal',
      'Pago': 'green',
      'Arquivado': 'red'
    };
    return colors[status] || 'gray';
  };

  const statusOptions = [
    'Não Contatou',
    'Pendente',
    'Em Análise',
    'Proposta Enviada',
    'Proposta Aceita',
    'Contrato Assinado',
    'Pago',
    'Arquivado'
  ];

  // Calculate statistics
  const totalValue = precatorios.reduce((sum, p) => sum + (p.valor_atualizado || 0), 0);
  const statusCounts = precatorios.reduce((acc, p) => {
    acc[p.status] = (acc[p.status] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center">
          <Box>
            <Flex align="center" mb={2}>
              <IconButton
                icon={<ArrowBackIcon />}
                onClick={() => navigate('/admin/precatorios/sp')}
                variant="ghost"
                aria-label="Voltar"
                mr={2}
              />
              <Text fontSize="3xl" fontWeight="bold" color={headingColor}>
                Precatórios Selecionados - SP
              </Text>
            </Flex>
            <Text color={textColor}>
              {precatorios.length} precatórios selecionados
            </Text>
          </Box>
          <HStack>
            <Button
              leftIcon={<DownloadIcon />}
              colorScheme="blue"
              onClick={handleDownloadSelected}
            >
              Download CSV
            </Button>
            <Button
              leftIcon={<RepeatIcon />}
              onClick={fetchPrecatorios}
              variant="outline"
            >
              Atualizar
            </Button>
          </HStack>
        </Flex>

        {/* Statistics */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
          <Box bg={bg} p={4} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
            <Text fontSize="sm" color={textColor}>Valor Total</Text>
            <Text fontSize="xl" fontWeight="bold">
              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalValue)}
            </Text>
          </Box>
          {Object.entries(statusCounts).slice(0, 3).map(([status, count]) => (
            <Box key={status} bg={bg} p={4} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
              <Text fontSize="sm" color={textColor}>{status}</Text>
              <Text fontSize="xl" fontWeight="bold">{count}</Text>
            </Box>
          ))}
        </SimpleGrid>

        {/* Filters and Actions */}
        <Box bg={sectionBg} p={6} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
          <VStack spacing={4} align="stretch">
            <HStack spacing={4} flexWrap="wrap">
              <InputGroup maxW="300px">
                <InputLeftElement pointerEvents="none">
                  <Icon as={SearchIcon} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Buscar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
              <Select
                maxW="200px"
                value={filterField}
                onChange={(e) => setFilterField(e.target.value)}
              >
                <option value="numero_precatorio">Número</option>
                <option value="beneficiario">Beneficiário</option>
                <option value="cpf_cnpj">CPF/CNPJ</option>
                <option value="ente">Ente</option>
              </Select>
              <Select
                maxW="200px"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">Todos os Status</option>
                {statusOptions.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </Select>
              <Button
                leftIcon={<ExternalLinkIcon />}
                colorScheme="purple"
                size="sm"
                onClick={() => navigate('/admin/precatorios/sp/sincronizar')}
              >
                Sincronizar JurisConsult
              </Button>
              <Button
                leftIcon={<ViewIcon />}
                colorScheme="orange"
                size="sm"
                onClick={() => navigate('/admin/precatorios/sp/alto-valor')}
              >
                Alto Valor
              </Button>
              <Button
                leftIcon={<CheckIcon />}
                colorScheme="green"
                size="sm"
                onClick={() => navigate('/admin/precatorios/sp/pendentes')}
              >
                Pendentes
              </Button>
            </HStack>

            {selectedItems.size > 0 && (
              <HStack spacing={4} p={4} bg={selectedBg} borderRadius="md">
                <Text fontWeight="medium">
                  {selectedItems.size} selecionados
                </Text>
                <Menu>
                  <MenuButton as={Button} rightIcon={<ChevronDownIcon />} size="sm">
                    Atualizar Status
                  </MenuButton>
                  <MenuList>
                    {statusOptions.map(status => (
                      <MenuItem key={status} onClick={() => handleBulkStatusUpdate(status)}>
                        <Badge colorScheme={getStatusColor(status)} mr={2}>
                          {status}
                        </Badge>
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                <Button
                  size="sm"
                  colorScheme="red"
                  variant="outline"
                  onClick={handleDesselecionar}
                >
                  Desmarcar Selecionados
                </Button>
              </HStack>
            )}
          </VStack>
        </Box>

        {/* Table */}
        <Box bg={bg} borderRadius="lg" borderWidth="1px" borderColor={borderColor} overflow="hidden">
          <TableContainer>
            <Table variant="simple" size="sm">
              <Thead bg={theadBg}>
                <Tr>
                  <Th width="50px">
                    <Checkbox
                      isChecked={
                        filteredPrecatorios.length > 0 &&
                        filteredPrecatorios.every(item => selectedItems.has(item._id))
                      }
                      onChange={handleSelectAll}
                    />
                  </Th>
                  <Th>Ordem</Th>
                  <Th>Número</Th>
                  <Th>Beneficiário</Th>
                  <Th>CPF/CNPJ</Th>
                  <Th>Ente</Th>
                  <Th>Natureza</Th>
                  <Th>Orçamento</Th>
                  <Th isNumeric>Valor</Th>
                  <Th>Status</Th>
                  <Th>Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredPrecatorios.map((item) => (
                  <Tr key={item._id} _hover={{ bg: hoverBg }}>
                    <Td>
                      <Checkbox
                        isChecked={selectedItems.has(item._id)}
                        onChange={() => handleSelectItem(item._id)}
                      />
                    </Td>
                    <Td>{item.ordem}</Td>
                    <Td>{item.numero_precatorio}</Td>
                    <Td>
                      <Tooltip label={item.beneficiario} placement="top">
                        <Text isTruncated maxW="200px">
                          {item.beneficiario}
                        </Text>
                      </Tooltip>
                    </Td>
                    <Td>{item.cpf_cnpj}</Td>
                    <Td>{item.ente}</Td>
                    <Td>{item.natureza}</Td>
                    <Td>{item.orcamento}</Td>
                    <Td isNumeric>
                      {new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                      }).format(item.valor_atualizado || 0)}
                    </Td>
                    <Td>
                      <Badge colorScheme={getStatusColor(item.status)}>
                        {item.status}
                      </Badge>
                    </Td>
                    <Td>
                      <IconButton
                        icon={<ViewIcon />}
                        size="sm"
                        variant="ghost"
                        aria-label="Ver detalhes"
                        onClick={() => handleOpenDetails(item, onOpen)}
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      </VStack>

      <PrecatoriosSelecionadasModal
        isOpen={isOpen}
        onClose={onClose}
        precatorio={selectedPrecatorio}
        loading={detailsLoading}
      />
    </Box>
  );
};

export default PrecatoriosSelecionadasSP;